/* Copyright (c) 2022 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import { DefaultAngularLand } from 'src/app/common/fancy-firebase/util/default-angular-land.service';
import {
	FancyListIndexDumpFactory,
	PriorityListFactory,
	ProjectListFactory,
	VarianceListFactory,
} from 'src/app/common/fancy-firebase/services/factories';
import { FancyIdJoin } from 'src/app/common/fancy-firebase/base/FancyIdJoin';
import { FancyFragmenter } from 'src/app/common/fancy-firebase/base/FancyFragmenter';
import { ProjectsApi } from 'src/app/app-data/state/ProjectsApi';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { ProjectObject } from 'src/app/common/fancy-firebase/objects/ProjectObject';
import {
	fancyFirebaseGetters,
	fancyFirebaseReferencer,
	ProjectCatalogType,
} from '@touchplan/fancy-referencer';
import { FancyFirebaseState } from 'src/app/common/fancy-firebase/services/fancy-firebase-state.service';
import { catchError, map, tap } from 'rxjs/operators';
import { EMPTY } from 'rxjs';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { ILoggerService } from '@touchplan/logging';

import firebase from 'firebase/compat/app';

@Injectable({
	providedIn: 'root',
})
export class FancyFirebaseProjectsApiBuilder {
	private logger: ILoggerService = LoggerServiceFactory.create('FancyFirebaseProjectsApiBuilder');

	constructor(
		private db: AngularFireDatabase,
		private defaultAngularLand: DefaultAngularLand,
		private projectListFactory: ProjectListFactory,
		private fancyListIndexDumpFactory: FancyListIndexDumpFactory,
		private varianceListFactory: VarianceListFactory,
		private priorityListFactory: PriorityListFactory,
		private fancyFrebaseState: FancyFirebaseState
	) {}

	public getProjectsApi(userId: string) {
		const projectList = this.makeProjectList(userId);
		return new ProjectsApi(projectList);
	}

	//TODO - extract this into some util code that accepts all the necessary data and builds this out
	// then port that style to plan.component. This doesn't functionally need an angular dependency
	// so it shouldn't have one.
	private makeProjectList(userId: string) {
		const angularland = this.defaultAngularLand;

		const refGetter = (projectId: string) => {
			return this.fancyFrebaseState.getProjectCatalog$(projectId, ProjectCatalogType.ProjectData).pipe(
				catchError((err) => {
					this.logger.error(err);
					return EMPTY;
				}),
				//add a distinct until changed here against the partition name?
				map(() =>
					fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
						fancyFirebaseGetters.getProjectsPath(projectId),
						projectId
					)
				)
			);
		};

		const projectFragment = new FancyFragmenter(angularland, refGetter);
		const projectList = this.projectListFactory.create(projectFragment.emitter$);
		projectFragment.start();

		// very hacky way, TODO for kurtis to fix it properly
		// new version, equivalently hacky, but allows deleting that legacy baseRef code
		const fakeProjectsRef = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
			'projects',
			null,
			ProjectCatalogType.Master
		);
		projectList.listRef = this.db.list(fakeProjectsRef);

		const projectFragmentJoiner = new FancyIdJoin('$id', projectList, projectFragment);

		const indicesUsersPath = fancyFirebaseGetters.getIndicesUsersPath(userId);
		const listIndexer = this.fancyListIndexDumpFactory.create(
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(indicesUsersPath),
			projectFragmentJoiner
		);

		projectList.addDestroyer(
			() => listIndexer.destroy(),
			() => projectFragmentJoiner.destroy(),
			() => projectFragment.destroy()
		);

		return projectList;
	}

	public getVarianceList(projectId: string) {
		const projectPath = this.getProjectPath(projectId);
		return this.varianceListFactory.create(
			fancyFirebaseReferencer
				.getLegacyRef<firebase.database.Reference>(projectPath, projectId)
				.child('variances')
				.orderByChild('varianceReason')
		);
	}

	public getPriorityList(projectId: string) {
		const projectPath = this.getProjectPath(projectId);
		return this.priorityListFactory.create(
			fancyFirebaseReferencer
				.getLegacyRef<firebase.database.Reference>(projectPath, projectId)
				.child('priorities')
		);
	}

	public getProjectObject(projectId: string) {
		const projectPath = this.getProjectPath(projectId);
		return new ProjectObject(
			this.defaultAngularLand,
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(projectPath, projectId)
		);
	}

	private getProjectPath(projectId: string) {
		return fancyFirebaseGetters.getProjectsPath(projectId);
	}
}
